import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaBox,
  FaShoppingCart,
  FaWarehouse,
  FaMapMarkerAlt,
  FaTruck,
  FaTags,
  FaCashRegister,
  FaChartBar,
  FaCog,
  FaStore,
  FaClipboardList,
  FaListAlt,
  FaChevronDown,
  FaChevronRight,
  FaUsers,
  FaSignOutAlt,
  FaFlask,
  FaBars,
  FaTimes,
  FaClipboard,
  FaFileInvoiceDollar,
  FaBell,
  FaSlidersH,
} from 'react-icons/fa';
import './css_layouts/sidebar.css';
import { useAuth } from '../contexts/AuthContext';
import { useStore } from '../contexts/StoreContext';

function Sidebar() {
  const [isCollapsed, setIsCollapsed]           = useState(false);
  const [isMobileOpen, setIsMobileOpen]         = useState(false);
  const [storeOpsOpen, setStoreOpsOpen]         = useState(false);
  const [inventoryOpen, setInventoryOpen]       = useState(false);
  const [orderingOpen, setOrderingOpen]         = useState(false);
  const [reportsOpen, setReportsOpen]           = useState(false);
  const [administrationOpen, setAdministrationOpen] = useState(false);
  const [storeDropdownOpen, setStoreDropdownOpen]   = useState(false);
  const [posOpen, setPosOpen]                       = useState(false); // ← nuevo

  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { stores, selectedStore, changeStore, loading } = useStore();

  const toggleSidebar        = () => {
    if (window.innerWidth <= 768) setIsMobileOpen(false);
    else setIsCollapsed(!isCollapsed);
  };
  const toggleMobileSidebar  = () => setIsMobileOpen(!isMobileOpen);
  const toggleStoreOps       = () => setStoreOpsOpen(!storeOpsOpen);
  const toggleInventory      = () => setInventoryOpen(!inventoryOpen);
  const toggleOrdering       = () => setOrderingOpen(!orderingOpen);
  const toggleReports        = () => setReportsOpen(!reportsOpen);
  const toggleAdministration = () => setAdministrationOpen(!administrationOpen);
  const toggleStoreDropdown  = () => setStoreDropdownOpen(!storeDropdownOpen);
  const togglePos            = () => setPosOpen(!posOpen); // ← nuevo

  const handleStoreChange = (store) => { changeStore(store); setStoreDropdownOpen(false); };
  const handleLogout      = () => { logout(); navigate('/login'); };
  const handleNavClick    = () => { if (window.innerWidth <= 768) setIsMobileOpen(false); };

  const isAdmin = user?.role === 'admin';

  // ── 1. Store Operations ──────────────────────────────────
  const storeOpsPaths = ['/productsbystore', '/recipes', '/preps', '/prepbystore', '/notifications'];
  const storeOpsItems = [
    { path: '/productsbystore', icon: <FaBox />,          label: 'Products by Store' },
    { path: '/recipes',         icon: <FaClipboardList />, label: 'Recipes' },
    { path: '/preps',           icon: <FaFlask />,         label: 'Pre-Batch' },
    { path: '/prepbystore',     icon: <FaFlask />,         label: 'Pre-Batch by Store' },
    { path: '/notifications',   icon: <FaBell />,          label: 'Notifications' }
  ];

  // ── 2. Inventory ─────────────────────────────────────────
  const inventoryPaths    = ['/inventories', '/physical-inventory'];
  const inventorySubItems = [
    { path: '/inventories',        icon: <FaWarehouse />, label: 'Inventories' },
    { path: '/physical-inventory', icon: <FaClipboard />, label: 'Physical Inventory' }
  ];

  // ── 3. Ordering ──────────────────────────────────────────
  const orderingPaths    = ['/ordering', '/view-orders', '/prep-ordering', '/view-prep-orders'];
  const orderingSubItems = [
    { path: '/ordering',    icon: <FaShoppingCart />, label: 'New Order' },
    { path: '/prep-ordering', icon: <FaFlask />,      label: 'Pre-Batch Ordering' },
    { path: '/view-prep-orders', icon: <FaListAlt />, label: 'View Prep Orders' },
    { path: '/view-orders', icon: <FaListAlt />,      label: 'View Orders' }
  ];

  // ── 4. POS ───────────────────────────────────────────────
  const posPaths    = ['/pos', '/modifiers'];  // ← paths con /
  const posSubItems = [
    { path: '/pos',       icon: <FaCashRegister />, label: 'POS' },
    { path: '/modifiers', icon: <FaSlidersH />,      label: 'Modifiers' }  // ← FaSliders más apropiado
  ];

  // ── 5. Administration ────────────────────────────────────
  const administrationItems = [
    { path: '/products',          icon: <FaBox />,          label: 'Products' },
    { path: '/locations',         icon: <FaMapMarkerAlt />, label: 'Locations' },
    { path: '/vendors',           icon: <FaTruck />,        label: 'Vendors' },
    { path: '/producttypes',      icon: <FaTags />,         label: 'Product Types' },
    { path: '/productcategories', icon: <FaListAlt />,      label: 'Product Categories' },
    { path: '/stores',            icon: <FaStore />,        label: 'Stores' },
    { path: '/users',             icon: <FaUsers />,        label: 'Users' }
  ];

  // Helper para renderizar un submenu genérico
  const renderSubmenu = (items) => (
    <div className="submenu">
      {items.map((item, i) => (
        <Link
          key={i}
          to={item.path}
          className={`submenu-item ${location.pathname === item.path ? 'active' : ''}`}
          onClick={handleNavClick}
        >
          <span className="submenu-icon">{item.icon}</span>
          <span className="submenu-label">{item.label}</span>
        </Link>
      ))}
    </div>
  );

  return (
    <>
      <button className="mobile-menu-btn" onClick={toggleMobileSidebar}><FaBars /></button>

      {isMobileOpen && <div className="sidebar-overlay" onClick={toggleMobileSidebar}></div>}

      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>

        {/* HEADER */}
        <div className="sidebar-header">
          <button className="mobile-close-btn" onClick={toggleMobileSidebar}><FaTimes /></button>
          <button className="toggle-btn" onClick={toggleSidebar}><FaBars /></button>

          {!isCollapsed && (
            <>
              <div className="user-info">
                <div className="user-name">{user?.name || 'User'}</div>
                <div className="user-role">{user?.role === 'admin' ? 'Administrator' : 'Employee'}</div>
              </div>

              <div className="header-store-selector">
                <button className="header-store-btn" onClick={toggleStoreDropdown} disabled={loading || stores.length === 0}>
                  <FaStore className="store-icon" />
                  <span className="store-name-header">
                    {loading ? 'Loading...' : (selectedStore ? selectedStore.name : 'Select Store')}
                  </span>
                  <FaChevronDown className={`chevron-header ${storeDropdownOpen ? 'open' : ''}`} />
                </button>

                {storeDropdownOpen && stores.length > 0 && (
                  <div className="store-dropdown-header">
                    {stores.map(store => (
                      <div
                        key={store.id}
                        className={`store-dropdown-item-header ${store.id === selectedStore?.id ? 'active' : ''}`}
                        onClick={() => handleStoreChange(store)}
                      >
                        {store.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {!loading && stores.length === 0 && (
                <div className="no-stores-message"><p>No stores assigned</p></div>
              )}
            </>
          )}
        </div>

        {/* NAV */}
        <nav className="sidebar-nav">

          {/* ── 1. Store Operations ── */}
          {selectedStore && (
            <div className="nav-item-with-submenu">
              <div
                className={`nav-item ${storeOpsPaths.some(p => location.pathname === p) ? 'active' : ''}`}
                onClick={toggleStoreOps}
              >
                <span className="nav-icon"><FaStore /></span>
                {!isCollapsed && (
                  <>
                    <span className="nav-label">Store Set Up</span>
                    <span className="nav-arrow">{storeOpsOpen ? <FaChevronDown /> : <FaChevronRight />}</span>
                  </>
                )}
              </div>
              {!isCollapsed && storeOpsOpen && renderSubmenu(storeOpsItems)}
            </div>
          )}

          {/* ── 2. Inventory ── */}
          {selectedStore && (
            <div className="nav-item-with-submenu">
              <div
                className={`nav-item ${inventoryPaths.some(p => location.pathname === p) ? 'active' : ''}`}
                onClick={toggleInventory}
              >
                <span className="nav-icon"><FaWarehouse /></span>
                {!isCollapsed && (
                  <>
                    <span className="nav-label">Inventory</span>
                    <span className="nav-arrow">{inventoryOpen ? <FaChevronDown /> : <FaChevronRight />}</span>
                  </>
                )}
              </div>
              {!isCollapsed && inventoryOpen && renderSubmenu(inventorySubItems)}
            </div>
          )}

          {/* ── 3. Ordering ── */}
          {selectedStore && (
            <div className="nav-item-with-submenu">
              <div
                className={`nav-item ${orderingPaths.some(p => location.pathname === p) ? 'active' : ''}`}
                onClick={toggleOrdering}
              >
                <span className="nav-icon"><FaShoppingCart /></span>
                {!isCollapsed && (
                  <>
                    <span className="nav-label">Ordering</span>
                    <span className="nav-arrow">{orderingOpen ? <FaChevronDown /> : <FaChevronRight />}</span>
                  </>
                )}
              </div>
              {!isCollapsed && orderingOpen && renderSubmenu(orderingSubItems)}
            </div>
          )}

          {/* ── 4. Invoices ── */}
          {selectedStore && (
            <Link
              to="/invoices"
              className={`nav-item ${location.pathname === '/invoices' ? 'active' : ''}`}
              onClick={handleNavClick}
            >
              <span className="nav-icon"><FaFileInvoiceDollar /></span>
              {!isCollapsed && <span className="nav-label">Invoices</span>}
            </Link>
          )}

          {/* ── 5. POS ── */}
          {selectedStore && (
            <div className="nav-item-with-submenu">
              <div
                className={`nav-item ${posPaths.some(p => location.pathname === p) ? 'active' : ''}`}
                onClick={togglePos}
              >
                <span className="nav-icon"><FaCashRegister /></span>
                {!isCollapsed && (
                  <>
                    <span className="nav-label">POS</span>
                    <span className="nav-arrow">{posOpen ? <FaChevronDown /> : <FaChevronRight />}</span>
                  </>
                )}
              </div>
              {!isCollapsed && posOpen && renderSubmenu(posSubItems)}
            </div>
          )}

          {/* ── 6. Reports ── */}
          {selectedStore && (
            <div className="nav-item-with-submenu">
              <div
                className={`nav-item ${location.pathname.startsWith('/reports') ? 'active' : ''}`}
                onClick={toggleReports}
              >
                <span className="nav-icon"><FaChartBar /></span>
                {!isCollapsed && (
                  <>
                    <span className="nav-label">Reports</span>
                    <span className="nav-arrow">{reportsOpen ? <FaChevronDown /> : <FaChevronRight />}</span>
                  </>
                )}
              </div>
              {!isCollapsed && reportsOpen && (
                <div className="submenu">
                  <Link to="/reports/usage"    className={`submenu-item ${location.pathname === '/reports/usage'    ? 'active' : ''}`} onClick={handleNavClick}><span className="submenu-label">Usage Report</span></Link>
                  <Link to="/reports/variance" className={`submenu-item ${location.pathname === '/reports/variance' ? 'active' : ''}`} onClick={handleNavClick}><span className="submenu-label">Variance Report</span></Link>
                </div>
              )}
            </div>
          )}

          {/* ── 7. Administration ── */}
          {isAdmin && (
            <div className="nav-item-with-submenu">
              <div
                className={`nav-item ${administrationItems.some(item => location.pathname === item.path) ? 'active' : ''}`}
                onClick={toggleAdministration}
              >
                <span className="nav-icon"><FaCog /></span>
                {!isCollapsed && (
                  <>
                    <span className="nav-label">Administration</span>
                    <span className="nav-arrow">{administrationOpen ? <FaChevronDown /> : <FaChevronRight />}</span>
                  </>
                )}
              </div>
              {!isCollapsed && administrationOpen && renderSubmenu(administrationItems)}
            </div>
          )}

        </nav>

        {/* FOOTER */}
        {!isCollapsed && (
          <div className="sidebar-footer">
            <button className="logout-btn" onClick={handleLogout}>
              <span className="nav-icon"><FaSignOutAlt /></span>
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default Sidebar;